/**
 * 관련법령 탭 — 검색어와 관련된 법 → 시행령 → 시행규칙 → 관련 고시(국가법령정보센터 목록).
 *   서버(/api/onto/related-laws)가 키워드별로 하루 한 번만 법령센터에서 받아 캐시한다.
 *   원문은 복사하지 않고 law.go.kr 로 안내만 한다. 분류 판정에는 쓰이지 않는다. (문의 #124)
 */
import React, { useEffect, useState } from "react";

export function useRelatedLaws(q) {
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!q) return undefined;
    const ctrl = new AbortController();
    setState({ loading: true, error: false, data: null });
    fetch(`/api/onto/related-laws?q=${encodeURIComponent(q)}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => setState({ loading: false, error: false, data }))
      .catch((e) => {
        if (e && e.name === "AbortError") return;
        setState({ loading: false, error: true, data: null });
      });
    return () => ctrl.abort();
  }, [q, retry]);
  return [state, () => setRetry((n) => n + 1)];
}

export function relatedLawCount(state) {
  if (!state || state.loading || state.error || !state.data) return null;
  return (state.data.laws || []).length + (state.data.rules || []).length;
}

function fmtDate(v) {
  const s = String(v || "");
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}` : s;
}

function Group({ title, items }) {
  if (!items.length) return null;
  return (
    <section aria-label={title} style={{ marginBottom: 16 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 13.5 }}>{title} <span style={{ color: "var(--audit-text-muted)", fontWeight: 500 }}>{items.length}</span></h3>
      {items.map((it) => (
        <div key={it.url} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "7px 0", borderTop: "1px solid var(--audit-border)", fontSize: 13 }}>
          <span style={{ flexShrink: 0, minWidth: 54, fontSize: 11.5, color: "var(--audit-text-muted)", border: "1px solid var(--audit-border)", borderRadius: 3, padding: "0 5px", textAlign: "center" }}>
            {it.kind || "-"}
          </span>
          <a href={it.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--audit-accent)", textDecoration: "none", flex: 1, minWidth: 0 }}>
            {it.name}
          </a>
          {it.effective && <span className="mono" style={{ fontSize: 12, color: "var(--audit-text-muted)", flexShrink: 0 }}>시행 {fmtDate(it.effective)}</span>}
        </div>
      ))}
    </section>
  );
}

export default function RelatedLawsPanel({ q, state, onRetry }) {
  const d = state.data;
  const laws = d?.laws || [];
  const rules = d?.rules || [];
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "2px 0 12px" }}>
        <h2 style={{ margin: 0, fontSize: 16, letterSpacing: "-.01em" }}>관련법령</h2>
        <span style={{ color: "var(--audit-text-muted)", fontSize: 12.5 }}>「{q}」 — 법 · 시행령 · 시행규칙 · 관련 고시 (국가법령정보센터)</span>
      </div>
      {state.error ? (
        <div style={{ fontSize: 13, color: "var(--audit-text-muted)", display: "flex", gap: 10, alignItems: "center" }}>
          불러오기 실패
          <button onClick={onRetry} style={{ border: "1px solid var(--audit-border)", background: "var(--audit-bg)", borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>재시도</button>
        </div>
      ) : state.loading ? (
        <div style={{ fontSize: 13, color: "var(--audit-text-muted)" }}>법령 목록을 불러오는 중…</div>
      ) : laws.length + rules.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--audit-text-muted)" }}>{d?.message || "검색어와 이름이 맞는 관련 법령이 없습니다."}</div>
      ) : (
        <>
          <Group title="법령 (법 · 시행령 · 시행규칙)" items={laws} />
          <Group title="관련 고시 · 훈령 · 예규" items={rules} />
          <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 8 }}>
            이름이 검색어와 맞는 목록이며, 개별 조문의 적용 여부는 원문에서 확인하세요. 링크는 국가법령정보센터(law.go.kr)로 연결됩니다.
          </div>
        </>
      )}
    </div>
  );
}
